import { logToNewRelic } from '../services/debug';

type GeoLocation = {
  coords: {
    accuracy: number | null;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    latitude: number;
    longitude: number;
    speed: number | null;
  };
  timestamp: number;
};

const GEO_OPTIONS: PositionOptions = {
  timeout: 3000,
  maximumAge: 60000,
};

export const isGeolocationAvailable = (): boolean => 'geolocation' in navigator;

export const isGeolocationEnabled = async (): Promise<boolean> => {
  if (!('geolocation' in navigator) || !('permissions' in navigator)) {
    return Promise.resolve(false);
  }
  const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
  return permissionStatus.state === 'granted';
};

export const getGeolocationPermissionState = async (): Promise<PermissionState | null> => {
  if (!('geolocation' in navigator) || !('permissions' in navigator)) {
    return null;
  }
  const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
  return permissionStatus?.state || null;
};

export const askForUserLocationPermission = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => {
        resolve(true);
      },
      () => {
        resolve(false);
      },
      GEO_OPTIONS,
    );
  });

export const getUserLocation = (): Promise<GeoLocation | null> =>
  new Promise((resolve) => {
    try {
      if (!('geolocation' in navigator) || !('permissions' in navigator)) {
        resolve(null);
        return;
      }
      void navigator.permissions
        .query({ name: 'geolocation' })
        .then((permissionStatus) => {
          if (permissionStatus.state === 'granted') {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                resolve(position);
              },
              () => {
                resolve(null);
              },
              GEO_OPTIONS,
            );
          } else {
            resolve(null);
          }
        })
        .catch(() => {
          resolve(null);
        });
    } catch (error) {
      const err = error as Error;
      void logToNewRelic('Failed to get user location', `Error in getUserLocation`, 'error', {
        error: {
          message: err.message,
        },
      });
      resolve(null);
    }
  });
